import { DealAction, DealFilters, DealStatus, DealType } from 'components/common/types/Deal.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { usePageFilters } from 'customHooks/usePageFilters';
import FilterArea from 'components/common/FilterArea';
import { dealTypesList } from 'consts/dealTypesList';
import { FilterAdvertiser, FilterDealType, FilterDsp, FilterStatus } from 'components/common/FilterArea/Filters';
import { DealTypeModel } from 'components/common/FilterArea/Filters/FilterDealType/FilterDealType.types';
import FilterDealNameOrId from './FilterDealNameOrId';
import OOCProgrammaticReportButton from '../OOCReport/OOCProgrammaticReportButton';

const filterItemClassName = 'w-full flex-1 min-w-[200px] basis-[200px]';

const isNGDealType = (dealType?: DealType): boolean =>
  dealType === DealType.NON_GUARANTEED_FLOOR_PRICE || dealType === DealType.NON_GUARANTEED_FIXED_PRICE;

const Filters = (): React.ReactElement => {
  const {
    changeFilters,
    filters: { dealNameOrId, status, dealType, dsp, advertiser, dismountedSearch },
  } = usePageFilters<DealFilters>('insights/');

  const STATUSES = [
    ...(useHasFeatureAccess(FeatureFlags.SIMPLIFIED_PLANNING) ? [{ code: DealStatus.DRAFT, name: 'Draft' }] : []),
    { code: DealStatus.PENDING_APPROVAL, name: 'Pending approval' },
    { code: DealStatus.APPROVED, name: 'Approved' },
    { code: DealStatus.REJECTED, name: 'Rejected' },
    { code: DealStatus.LIVE, name: 'Live' },
    { code: DealStatus.ENDED, name: 'Ended' },
    { code: DealStatus.CANCELLED, name: 'Cancelled' },
    { code: DealStatus.TERMINATED, name: 'Terminated' },
    { code: DealStatus.FAILED, name: 'Failed' },
    ...(useHasFeatureAccess(FeatureFlags.DV360)
      ? [
          { code: DealStatus.PENDING_RESERVATION, name: 'Pending Reservation' },
          { code: DealStatus.RESERVED, name: 'Reserved' },
        ]
      : []),
  ];

  const isUserHasOOCFeatureAccess = useHasFeatureAccess(FeatureFlags.OUT_OF_CHARGE);

  const handleDealTypeChange = ({ dealType: newDealType }: Partial<DealTypeModel>): void => {
    if (isNGDealType(newDealType) && dismountedSearch === DealAction.ACTIVE) {
      changeFilters({ dismountedSearch: DealAction.NONE });
    }
    changeFilters({ dealType: newDealType });
  };

  return (
    <FilterArea customWrapperStyles="w-full h-full flex flex-col gap-4">
      <div className="flex flex-wrap gap-4 items-center w-full">
        <div className={filterItemClassName}>
          <FilterDsp selectedValue={dsp} onChange={(name, value) => changeFilters({ [name]: value })} />
        </div>
        <div className={filterItemClassName}>
          <FilterDealNameOrId changeFilters={changeFilters} selectedDealNameOrId={dealNameOrId} />
        </div>
        <div className={filterItemClassName}>
          <FilterAdvertiser changeFilters={changeFilters} selectedAdvertiser={advertiser} />
        </div>
        <div className={filterItemClassName}>
          <FilterStatus changeFilters={changeFilters} status={status} statusesList={STATUSES} />
        </div>
        <div className={filterItemClassName}>
          <FilterDealType dealType={dealType} changeFilters={handleDealTypeChange} dealTypesList={dealTypesList} />
        </div>
      </div>
      {isUserHasOOCFeatureAccess && (
        <div className="flex items-start gap-4 w-full min-w-0">
          <div className="flex-1 min-w-0" />
          <div className="shrink-0 self-center">
            <OOCProgrammaticReportButton
              onClick={() =>
                changeFilters({
                  dismountedSearch: dismountedSearch === DealAction.ACTIVE ? DealAction.NONE : DealAction.ACTIVE,
                })
              }
              dismountedSearch={dismountedSearch}
              isNGDealFilterApplied={isNGDealType(dealType)}
            />
          </div>
        </div>
      )}
    </FilterArea>
  );
};

export default Filters;
